import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Search, AlertTriangle, Package } from 'lucide-react';
import { fetchWithAuth } from '../utils/api';
import { useLanguage } from './LanguageContext';
import { calculateLine, resolveVatRate } from '../utils/vat';
import { branchVatRate as branchRate } from '../utils/vatConfig';
import Pagination from './Pagination';

const BLANK = {
  name: '', name_th: '', description: '', category_id: '', brand: '', unit: '',
  cost_price: '', sale_price: '', vat_mode: 'none', vat_rate: '',
  stock_quantity: '0', reorder_level: '0', status: 'active',
};

const Shop = ({ currentBranch }) => {
  const { print_lang, currentLanguage } = useLanguage();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState(BLANK);
  const [formError, setFormError] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const branchId = currentBranch?.branch_id;
  // A product with no rate override inherits the branch rate; a stored 0 does not.
  const branchVatRate = branchRate(currentBranch);

  const catName = (row) =>
    currentLanguage === 'th'
      ? (row.category_name_th || row.category_name_en || '-')
      : (row.category_name_en || '-');

  const productName = (row) =>
    currentLanguage === 'th' ? (row.name_th || row.name) : row.name;

  const fetchProducts = useCallback(async () => {
    if (!branchId) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        branch_id: branchId,
        page: currentPage,
        limit: pageSize,
        status: 'all',
      });
      if (searchTerm) params.set('search', searchTerm);
      if (categoryFilter) params.set('category_id', categoryFilter);
      const res = await fetchWithAuth(`/api/shop/products?${params}`);
      setProducts(res?.data || []);
      setTotalPages(res?.pagination?.totalPages || 0);
      setTotalCount(res?.pagination?.totalCount || 0);
    } catch (err) {
      console.error('Failed to load products:', err);
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  }, [branchId, currentPage, pageSize, searchTerm, categoryFilter]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  useEffect(() => {
    if (!branchId) return;
    fetchWithAuth('/api/shop/categories')
      .then((res) => setCategories(res?.data || []))
      .catch(() => setCategories([]));
    fetchWithAuth(`/api/shop/products/low-stock?branch_id=${branchId}`)
      .then((res) => setLowStockCount((res?.data || []).length))
      .catch(() => setLowStockCount(0));
  }, [branchId, products.length]);

  const openAdd = () => {
    setModalMode('add'); setSelected(null); setFormData(BLANK); setFormError('');
    setShowModal(true);
  };

  const openEdit = (p) => {
    setModalMode('edit'); setSelected(p); setFormError('');
    setFormData({
      name: p.name || '', name_th: p.name_th || '', description: p.description || '',
      category_id: p.category_id ?? '', brand: p.brand || '', unit: p.unit || '',
      cost_price: p.cost_price ?? '', sale_price: p.sale_price ?? '',
      vat_mode: p.vat_mode || 'none',
      // '' means inherit; a stored 0 must render as 0, so use ?? not ||.
      vat_rate: p.vat_rate ?? '',
      stock_quantity: p.stock_quantity ?? '0', reorder_level: p.reorder_level ?? '0',
      status: p.status || 'active',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!formData.name.trim()) {
      setFormError(print_lang('product_name_required', 'Product name is required'));
      return;
    }
    try {
      const body = { ...formData, branch_id: branchId };
      if (modalMode === 'edit') {
        await fetchWithAuth(`/api/shop/products/${selected.product_id}`, {
          method: 'PUT', body: JSON.stringify(body),
        });
      } else {
        await fetchWithAuth('/api/shop/products', {
          method: 'POST', body: JSON.stringify(body),
        });
      }
      setShowModal(false);
      fetchProducts();
    } catch (err) {
      setFormError(err.message || print_lang('save_failed', 'Save failed'));
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(print_lang('confirm_delete', 'Delete this item?'))) return;
    try {
      await fetchWithAuth(`/api/shop/products/${p.product_id}`, { method: 'DELETE' });
      fetchProducts();
    } catch (err) {
      window.alert(
        err.code === 'PRODUCT_IN_USE'
          ? print_lang('product_in_use', 'This product has been sold and cannot be deleted')
          : (err.message || print_lang('delete_failed', 'Delete failed'))
      );
    }
  };

  // What the customer actually pays for one unit, under this row's VAT mode.
  const priceInclVat = (row) => {
    const rate = resolveVatRate({
      vatMode: row.vat_mode, itemRate: row.vat_rate, branchRate: branchVatRate,
    });
    return calculateLine({
      quantity: 1, unit_price: row.sale_price, vat_mode: row.vat_mode, vat_rate: rate,
    }).total;
  };

  const vatLabel = (mode) => ({
    include: print_lang('vat_include', 'Price includes VAT'),
    exclude: print_lang('vat_exclude', 'Price excludes VAT'),
    none: print_lang('vat_none', 'No VAT'),
  }[mode] || print_lang('vat_none', 'No VAT'));

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">{print_lang('shop', 'Shop')}</h1>
          {lowStockCount > 0 && (
            <span className="badge badge-warning flex items-center gap-1">
              <AlertTriangle size={14} />
              {lowStockCount} {print_lang('low_stock', 'Low Stock')}
            </span>
          )}
        </div>
        <button className="btn btn-primary flex items-center gap-2" onClick={openAdd}>
          <Plus size={18} /> {print_lang('add_product', 'Add Product')}
        </button>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            className="w-full border rounded pl-10 pr-3 py-2"
            placeholder={print_lang('search_products', 'Search products...')}
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          />
        </div>
        <select
          className="border rounded px-3 py-2"
          value={categoryFilter}
          onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
        >
          <option value="">{print_lang('all_categories', 'All Categories')}</option>
          {categories.map((c) => (
            <option key={c.category_id} value={c.category_id}>
              {currentLanguage === 'th' ? (c.name_th || c.name_en) : c.name_en}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-500">{print_lang('loading', 'Loading...')}</div>
      ) : products.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Package size={40} className="mx-auto mb-2 text-gray-300" />
          {print_lang('no_products', 'No products found')}
        </div>
      ) : (
        <>
          <table>
            <thead>
              <tr>
                <th>{print_lang('product_name', 'Product Name')}</th>
                <th>{print_lang('category', 'Category')}</th>
                <th>{print_lang('unit', 'Unit')}</th>
                <th className="text-right">{print_lang('sale_price', 'Sale Price')}</th>
                <th>{print_lang('vat_mode', 'VAT')}</th>
                <th className="text-right">{print_lang('price_incl_vat', 'Price incl. VAT')}</th>
                <th className="text-right">{print_lang('stock_quantity', 'Stock')}</th>
                <th>{print_lang('status', 'Status')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => {
                const low = Number(p.stock_quantity) <= Number(p.reorder_level);
                return (
                  <tr key={p.product_id}>
                    <td>
                      <div className="font-medium">{productName(p)}</div>
                      {p.brand && <div className="text-sm text-gray-500">{p.brand}</div>}
                    </td>
                    <td>{catName(p)}</td>
                    <td>{p.unit || '-'}</td>
                    <td className="text-right">{Number(p.sale_price).toFixed(2)}</td>
                    <td><span className="text-sm">{vatLabel(p.vat_mode)}</span></td>
                    <td className="text-right font-medium">{priceInclVat(p).toFixed(2)}</td>
                    <td className={`text-right ${low ? 'text-red-600 font-semibold' : ''}`}>
                      {Number(p.stock_quantity).toFixed(0)}
                    </td>
                    <td>
                      <span className={`badge ${p.status === 'active' ? 'badge-success' : 'badge-error'}`}>
                        {print_lang(p.status, p.status)}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-1 justify-end">
                        <button className="p-2 hover:bg-gray-100 rounded" onClick={() => openEdit(p)}
                                title={print_lang('edit', 'Edit')}>
                          <Edit2 size={16} className="text-blue-600" />
                        </button>
                        <button className="p-2 hover:bg-red-50 rounded" onClick={() => handleDelete(p)}
                                title={print_lang('delete', 'Delete')}>
                          <Trash2 size={16} className="text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
            print_lang={print_lang}
          />
        </>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) setShowModal(false);
        }}>
          <div className="modal-content w-full max-w-3xl p-6">
            <h2 className="text-xl font-bold mb-4">
              {modalMode === 'edit'
                ? print_lang('edit_product', 'Edit Product')
                : print_lang('add_product', 'Add Product')}
            </h2>

            {formError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4">
                {formError}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('product_name', 'Product Name')} *
                  </label>
                  <input type="text" className="w-full border rounded px-3 py-2" required
                         value={formData.name}
                         onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('name_th', 'Name (Thai)')}
                  </label>
                  <input type="text" className="w-full border rounded px-3 py-2"
                         value={formData.name_th}
                         onChange={(e) => setFormData({ ...formData, name_th: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('category', 'Category')}
                  </label>
                  <select className="w-full border rounded px-3 py-2" value={formData.category_id}
                          onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}>
                    <option value="">-</option>
                    {categories.map((c) => (
                      <option key={c.category_id} value={c.category_id}>
                        {currentLanguage === 'th' ? (c.name_th || c.name_en) : c.name_en}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{print_lang('brand', 'Brand')}</label>
                    <input type="text" className="w-full border rounded px-3 py-2" value={formData.brand}
                           onChange={(e) => setFormData({ ...formData, brand: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">{print_lang('unit', 'Unit')}</label>
                    <input type="text" className="w-full border rounded px-3 py-2" value={formData.unit}
                           onChange={(e) => setFormData({ ...formData, unit: e.target.value })} />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{print_lang('cost_price', 'Cost Price')}</label>
                  <input type="number" step="0.01" min="0" className="w-full border rounded px-3 py-2"
                         value={formData.cost_price}
                         onChange={(e) => setFormData({ ...formData, cost_price: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{print_lang('sale_price', 'Sale Price')}</label>
                  <input type="number" step="0.01" min="0" className="w-full border rounded px-3 py-2"
                         value={formData.sale_price}
                         onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{print_lang('stock_quantity', 'Stock')}</label>
                  <input type="number" step="1" min="0" className="w-full border rounded px-3 py-2"
                         value={formData.stock_quantity}
                         onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{print_lang('reorder_level', 'Reorder Level')}</label>
                  <input type="number" step="1" min="0" className="w-full border rounded px-3 py-2"
                         value={formData.reorder_level}
                         onChange={(e) => setFormData({ ...formData, reorder_level: e.target.value })} />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-gray-50 rounded">
                <div>
                  <label className="block text-sm font-medium mb-1">{print_lang('vat_mode', 'VAT')}</label>
                  <select className="w-full border rounded px-3 py-2" value={formData.vat_mode}
                          onChange={(e) => setFormData({ ...formData, vat_mode: e.target.value })}>
                    <option value="none">{print_lang('vat_none', 'No VAT')}</option>
                    <option value="include">{print_lang('vat_include', 'Price includes VAT')}</option>
                    <option value="exclude">{print_lang('vat_exclude', 'Price excludes VAT')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('vat_rate_override', 'VAT Rate Override')}
                  </label>
                  <input type="number" step="0.01" min="0" max="100"
                         className="w-full border rounded px-3 py-2"
                         placeholder={`${print_lang('use_branch_rate', 'Use branch rate')} (${branchVatRate}%)`}
                         disabled={formData.vat_mode === 'none'}
                         value={formData.vat_rate}
                         onChange={(e) => setFormData({ ...formData, vat_rate: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('price_incl_vat', 'Price incl. VAT')}
                  </label>
                  <div className="px-3 py-2 font-medium">
                    {calculateLine({
                      quantity: 1,
                      unit_price: formData.sale_price === '' ? 0 : formData.sale_price,
                      vat_mode: formData.vat_mode,
                      vat_rate: resolveVatRate({
                        vatMode: formData.vat_mode,
                        itemRate: formData.vat_rate,
                        branchRate: branchVatRate,
                      }),
                    }).total.toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">{print_lang('status', 'Status')}</label>
                <select className="w-full border rounded px-3 py-2" value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                  <option value="active">{print_lang('active', 'Active')}</option>
                  <option value="inactive">{print_lang('inactive', 'Inactive')}</option>
                </select>
              </div>

              <div className="flex justify-end gap-2">
                <button type="button" className="btn" onClick={() => setShowModal(false)}>
                  {print_lang('cancel', 'Cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {print_lang('save', 'Save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shop;
